import React from 'react';

const apps = [
  {
    name: 'StockPile',
    tagline: 'Inventory for businesses that sell physical stuff',
    description: 'Track stock across multiple locations, know what\'s running low, receive deliveries and fix variances, run cycle counts and full stocktakes — plus batch traceability and kit building. No complex setup. Live quickly with a clear view of your inventory.',
    url: 'https://www.stockpile.site/',
    icon: '📦',
    featured: true,
  },
  {
    name: 'FleetBase',
    tagline: 'Fleet compliance, simplified',
    description: 'Track MOTs, insurance, LOLER inspections, and service schedules across your entire fleet. Never miss a compliance deadline again.',
    url: 'https://fleetbase.tech/',
    icon: '🚛',
    featured: false,
  },
  {
    name: 'WellBook',
    tagline: 'Built for health & wellness businesses',
    description: 'Your booking system, up and running in minutes. Handles appointments, group classes, facility bookings (courts, pitches, studios), online payments, and client management — all under your brand. Add two links to your site and you\'re live.',
    url: 'https://www.wellbook.tech/',
    icon: '📅',
    featured: false,
  },
  {
    name: 'SafeComply',
    tagline: 'CDM 2015 · Health & safety compliance, audit-ready',
    description: 'Health & safety evidence for UK principal contractors. Record assessments, assemble client packs, share approval links—audit-ready in one place. Clients approve RAMS via link, no login required.',
    url: 'https://www.safecomply.app/',
    icon: '🛡️',
    featured: false,
  },
  {
    name: 'Quoteq',
    tagline: 'Quote to cash, without the chaos',
    description: 'Quotes, invoices, and payments in one place. Customers accept and pay online via Stripe—when they pay, Xero updates automatically. No manual entry, no chasing between systems.',
    url: 'https://www.quoteq.me/',
    icon: '💰',
    featured: false,
  },
  {
    name: 'Waypost',
    tagline: 'Delivery operations for product-based SMEs',
    description: 'From order to delivered in one place. Plan routes, dispatch drivers, capture proof of delivery, and trigger invoicing. No spreadsheets. No WhatsApp chaos.',
    url: 'https://www.waypost.tech/',
    icon: '🚚',
    featured: false,
  },
];

function AppCard({ app, spanFull }) {
  const isFeatured = app.featured;

  return (
    <a
      href={app.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block text-left transition-all duration-300 hover:shadow-xl hover:shadow-zapt-purple/15 ${
        isFeatured || spanFull ? 'col-span-1 lg:col-span-2' : ''
      }`}
    >
      <div className={`h-full rounded-2xl border bg-zapt-purple/10 backdrop-blur-sm border-zapt-purple/30 hover:border-zapt-purple/50 transition-colors overflow-hidden ${
        (isFeatured || spanFull) ? 'p-6 sm:p-8 lg:p-10' : 'p-6 sm:p-7'
      }`}>
        <div className={`flex flex-col sm:flex-row gap-4 sm:gap-6 ${(isFeatured || spanFull) ? 'lg:gap-8' : ''}`}>
          {/* Icon - above text on mobile, left of text on larger screens */}
          <div className={`flex-shrink-0 rounded-2xl bg-zapt-purple/30 flex items-center justify-center transition-colors group-hover:bg-zapt-purple/40 self-start ${
            (isFeatured || spanFull) ? 'w-12 h-12 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-2xl sm:text-5xl lg:text-5xl' : 'w-10 h-10 sm:w-16 sm:h-16 text-xl sm:text-4xl'
          }`}>
            {app.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 gap-y-1 mb-2">
              <h3 className={`font-bold text-white ${(isFeatured || spanFull) ? 'text-xl sm:text-2xl lg:text-3xl' : 'text-lg sm:text-xl'}`}>
                {app.name}
              </h3>
              {app.badge && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zapt-yellow/20 text-zapt-yellow border border-zapt-yellow/40">
                  {app.badge}
                </span>
              )}
            </div>
            <p className="text-zapt-yellow font-medium mb-2 text-sm sm:text-base">
              {app.tagline}
            </p>
            <p className={`text-gray-300 mb-4 ${(isFeatured || spanFull) ? 'text-base sm:text-lg leading-relaxed' : 'text-sm sm:text-base'} ${
              (isFeatured || spanFull) ? 'lg:max-w-2xl' : ''
            }`}>
              {app.description}
            </p>
            <span className="inline-flex items-center gap-2 text-zapt-yellow font-medium text-sm sm:text-base group-hover:gap-3 transition-all">
              {app.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              <span aria-hidden className="opacity-80">↗</span>
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}

export default function AppsWeveBuiltSection() {
  const featured = apps.find(a => a.featured);
  const others = apps.filter(a => !a.featured);

  return (
    <section id="apps-weve-built" className="py-16 sm:py-24 bg-zapt-navy border-t border-white/10 relative z-10 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-4">
          What we build
        </h2>
        <p className="text-gray-400 text-center mb-4 max-w-2xl mx-auto text-base sm:text-lg">
          We create quality custom software for businesses. From sophisticated platforms to standalone apps, we tailor your tech exactly to suit the way you work, built fast (in days), affordably and always maintained for you.
        </p>
        <p className="text-gray-400 text-center mb-12 sm:mb-16 max-w-2xl mx-auto text-base sm:text-lg">
          Also check out our specialist <span className="text-zapt-yellow font-medium">subscription apps</span> that anyone can sign up to and use right away:
        </p>

        {/* Desktop: Featured card full width, then 2-col grid */}
        {/* Mobile: Single column stack */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {featured && <AppCard app={featured} />}
          {others.map((app, index) => (
            <AppCard
              key={app.name}
              app={app}
              spanFull={others.length % 2 === 1 && index === others.length - 1}
            />
          ))}
        </div>

        <p className="text-gray-500 text-center mt-10 text-sm">
          More subscription apps in the pipeline.
        </p>
      </div>
    </section>
  );
}
